import styled, { css } from 'styled-components';

import { AFFILIATE_FORM, ANIM, COLORS, HOME, MOBILE, SPACE, WEBSITE_ANALYTICS } from '../../../../theme/tokens';
import { Text } from '../../../../components';

export const Root = styled.div`
  position: relative;
  min-width: 0;
  max-width: 100%;

  /* On a phone the panel opens across the whole header row (Controls), not under the pill. */
  ${MOBILE} {
    position: static;
  }
`;

/** The pill that opens the picker: the same pill as the period selector beside it. */
export const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-height: 48px;
  max-width: 280px;
  color: ${HOME.t50};

  ${MOBILE} {
    max-width: 100%;
  }
  border: 2px solid ${HOME.intervalBorder};
  background: transparent;
  border-radius: 100px;
  padding: ${SPACE[0]} 14px; /* off-scale on purpose: the period pill's inset, so the two pills match */
  cursor: pointer;
`;

export const TriggerLabel = styled.span`
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${HOME.t50};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  transition: transform ${ANIM.INTERACT_MS}ms ${HOME.ease};
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});

  svg {
    display: block;
  }
`;

/** The panel under the pill: a search field above a scrolling list of pages. */
export const Panel = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 40;
  width: min(420px, calc(100vw - 32px));
  max-height: min(70vh, 560px);
  display: flex;
  flex-direction: column;
  background: ${HOME.menuBg};
  border: 1px solid ${HOME.hairline};
  border-radius: 16px;
  box-shadow: ${HOME.menuShadow};
  overflow: hidden;

  ${MOBILE} {
    left: 0;
    right: 0;
    width: auto;
  }
`;

export const SearchRow = styled.label`
  flex-shrink: 0; /* the list below scrolls; the field keeps its height */
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  margin: ${SPACE[8]};
  padding: ${SPACE[0]} ${SPACE[12]};
  height: 40px;
  border-radius: 10px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.textFaint};

  &:focus-within {
    border-color: ${COLORS.hoverBorderLit};
  }
`;

/** The search field pinned to the top of a filter's scrolling choices (the filters sheet), on the sheet's own surface
 *  so the choices pass behind it. */
export const StickySearch = styled.div`
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex; /* a flex box keeps the field's margin inside the backing (no margin collapse) */
  flex-direction: column;
  background: ${AFFILIATE_FORM.surface};
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: 0;
  background: transparent;
  color: ${COLORS.text};
  font-size: 14px;

  &::placeholder {
    color: ${COLORS.textFaint};
  }
  &:focus {
    outline: none;
  }
`;

export const Scroll = styled.div`
  min-height: 0;
  overflow-y: auto;
  padding: ${SPACE[0]} ${SPACE[8]} ${SPACE[8]};
`;

export const SectionLabel = styled(Text)`
  display: block;
  padding: ${SPACE[12]} ${SPACE[8]} ${SPACE[4]};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${HOME.t500};
`;

/**
 * A choice's checkbox in a list that takes several (the filters' pages, countries, sources, links): the shared
 * Checkbox's box, painted only. The shared one is a labelled input, which cannot sit inside a choice's button; the
 * choice itself carries the selected state.
 */
export const TickBox = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  box-sizing: border-box;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid ${(p) => (p.$checked ? COLORS.ctaSolid : COLORS.textSubtle)};
  background: ${(p) => (p.$checked ? COLORS.ctaSolid : 'transparent')};
  color: ${COLORS.ctaSolidText};
  transition:
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
`;

/** One page to pick: a picture (landing pages), its name and address, and its views in the period. */
export const Row = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  width: 100%;
  min-height: 48px;
  padding: ${SPACE[8]};
  border: 0;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${HOME.menuHoverBg};
  }

  ${(p) =>
    p.$active &&
    css`
      background: ${HOME.menuActiveBg};
    `}
`;

export const Thumb = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  background: ${WEBSITE_ANALYTICS.glyphBg};
  color: ${WEBSITE_ANALYTICS.glyph};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const RowText = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
`;

export const RowName = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: ${HOME.t100};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowSub = styled(Text)`
  font-size: 12px;
  color: ${HOME.t500};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowValue = styled(Text)`
  flex-shrink: 0;
  font-size: 12px;
  color: ${HOME.t400};
  white-space: nowrap;
`;

export const Note = styled(Text)`
  display: block;
  padding: ${SPACE[8]} ${SPACE[8]} ${SPACE[12]};
  font-size: 13px;
  color: ${HOME.t400};
`;
